/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import Logger from "../../../clients/logger";
import LauncherClient from "../../../clients/launcherClient";
import { BootTaskCallbackInterface } from "../types";
import { System } from "../../../common/system";
import configClient from "../../../clients/configClient";
import FSBLDependencyManager from "../../../common/dependencyManager";
import { getMonitorFromScaledXY, getTaskbar } from "../../../common/Monitors";
import { ShowWindowParams, SpawnParams } from "../../window/types";
import { MonitorCoordinates, MonitorTaskbar } from "../../../FEA";
import { bringWindowsToFront } from "../../../common/Desktop";

/**
 * Boot task that loads system tray icon.  Can assume LauncherClient is ready when this task runs.  That task also handles shutdown, removing system tray icon.
 * @private
 */
export class LoadSystemTrayIconTask {
	finsembleConfig: any;

	public async start(doneCallback: BootTaskCallbackInterface) {
		Logger.system.debug("loadSystemTrayIconTask start");
		FSBLDependencyManager.shutdown.waitFor({}, this.handleShutdown.bind(this));

		const { data: systemTrayIconURL } = await configClient.getValue({
			field: "finsemble.systemTrayIcon",
		});
		let { data: systemTrayComponent } = await configClient.getValue({
			field: "finsemble.systemTrayComponent",
		});

		// Listen for dynamic updates to config entry for new system tray icon URL
		configClient.addListener({ field: "finsemble.systemTrayIcon" }, (addListenerErr, data) => {
			// update icon using new icon url value
			let newIconURL = data.value;

			Logger.system.log("Setting system tray icon", newIconURL);
			System.setTrayIcon(newIconURL);
		});

		if (systemTrayIconURL && systemTrayComponent) {
			Logger.system.log("Setting system tray icon", systemTrayIconURL);
			System.setTrayIcon(systemTrayIconURL);
		}

		if (systemTrayComponent) this.setupSystemTrayListeners(systemTrayComponent);
		doneCallback("loadSystemTrayIconTask", "bootTasks", "completed");
	}

	/**
	 * setupSystemTray
	 */
	private setupSystemTrayListeners(systemTrayComponent: string) {
		const leftClick = () => {
			LauncherClient.getActiveDescriptors((getActiveDescriptorsError, response) => {
				if (!getActiveDescriptorsError && response) {
					const windowList = Object.keys(response);
					bringWindowsToFront({ windowList });
				}
			});
		};

		const computeFromTaskbar = ({
			spawnParams,
			taskbar,
			md,
			mouseX,
			mouseY,
		}: {
			spawnParams: SpawnParams;
			taskbar: MonitorTaskbar;
			md: MonitorCoordinates;
			mouseX: number;
			mouseY: number;
		}): SpawnParams => {
			// Am I closer to the left?
			if (mouseX - md.left <= md.right - mouseX) {
				spawnParams.left = mouseX;
				if (taskbar.edge === "left" && spawnParams.left < taskbar.right) {
					spawnParams.left = taskbar.right + 1;
				}
				// Or closer to the right...
			} else {
				if (taskbar.edge === "right" && mouseX < taskbar.right) {
					mouseX = taskbar.right - 1;
				}
				spawnParams.right = md.right - mouseX; // CSS style, pixels from right edge of monitor
			}
			// Am I closer to the top?
			if (mouseY - md.top <= md.bottom - mouseY) {
				spawnParams.top = mouseY;
				if (taskbar.edge === "top" && spawnParams.top < taskbar.top) {
					spawnParams.top = taskbar.top + 1;
				}
				// Or closer to the bottom...
			} else {
				if (taskbar.edge === "bottom" && mouseY > taskbar.top) {
					mouseY = taskbar.top - 1;
				}
				spawnParams.bottom = md.bottom - mouseY; // CSS style, pixels from bottom edge of monitor
			}
			return spawnParams;
		};

		const rightClick = async () => {
			const mousePosition = await System.getMousePosition();

			let mouseX: number = mousePosition?.left ?? 0;
			let mouseY: number = mousePosition?.top ?? 0;
			const monitor = await getMonitorFromScaledXY(mouseX, mouseY);
			let spawnParams: ShowWindowParams = {
				position: "monitor",
				spawnIfNotFound: true,
				monitor: monitor.position,
			};

			const taskbar = getTaskbar(monitor);

			if (taskbar) {
				const md = monitor.monitorRect;
				spawnParams = computeFromTaskbar({ spawnParams, taskbar, md, mouseX, mouseY });
			}
			if (systemTrayComponent) LauncherClient.showWindow({ componentType: systemTrayComponent }, spawnParams);
		};

		System.addEventListener("system-tray-click", () => {
			leftClick();
		});
		System.addEventListener("system-tray-right-click", () => {
			rightClick();
		});
	}

	private handleShutdown() {
		System.Window.getCurrent().removeTrayIcon(
			() => {
				Logger.system.debug("System Tray Icon removed");
			},
			() => {
				Logger.system.error("Unable to remove System Tray Icon");
			}
		);
	}
}
